import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { Logo } from "./Brand";
import { ThemeToggle } from "./ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { useContent } from "../../context/SiteContext";

const LINKS = [
  ["/", "Home"],
  ["/services", "Services"],
  ["/reviews", "Reviews"],
  ["/contact", "Contact Us"],
];

export function Navbar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const cls = ({ isActive }) =>
    `font-cond font-bold uppercase tracking-wider text-[15px] transition-colors ${isActive ? "text-[#00d9ff]" : "text-slate-200 hover:text-white"}`;
  const authBtn = user ? (
    <Link to="/dashboard" className="btn-ghost !py-2 !px-4 text-sm" data-testid="nav-dashboard-btn">
      <LayoutDashboard className="w-4 h-4" /> Dashboard
    </Link>
  ) : (
    <Link to="/login" className="btn-ghost !py-2 !px-4 text-sm" data-testid="nav-login-btn">Login</Link>
  );
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#03060f]/70 backdrop-blur-xl border-b border-[#007bff]/20" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map(([to, l]) => (
            <NavLink key={to} to={to} end className={cls} data-testid={`nav-link-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          {authBtn}
          <Link to="/quote" className="btn-electric !py-2 !px-4 text-sm" data-testid="nav-quote-btn">Get a Quote</Link>
        </div>
        <div className="md:hidden flex items-center gap-1">
        <ThemeToggle />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger className="md:hidden p-2 text-white" data-testid="mobile-menu-btn"><Menu className="w-7 h-7" /></SheetTrigger>
          <SheetContent side="right" className="bg-[#050a18] border-[#007bff]/30 w-[85vw] max-w-sm">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-8 flex flex-col gap-6" onClick={() => setOpen(false)}>
              <Logo />
              {LINKS.map(([to, l]) => (
                <NavLink key={to} to={to} end className={({ isActive }) => `${cls({ isActive })} text-2xl`} data-testid={`mobile-nav-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</NavLink>
              ))}
              <Link to="/quote" className="btn-electric" data-testid="mobile-nav-quote">Get a Quote</Link>
              {authBtn}
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const f = useContent("footer");
  const c = useContent("contact");
  return (
    <footer className="border-t border-[#007bff]/20 bg-[#02040b]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo className="text-3xl" />
          <p className="mt-3 text-slate-400 max-w-sm">{f.tagline}</p>
        </div>
        <div>
          <h4 className="font-cond font-bold uppercase tracking-widest text-[#00d9ff] text-sm mb-3">Explore</h4>
          <ul className="space-y-2 text-slate-300">
            {[...LINKS, ["/quote", "Get a Quote"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="hover:text-white">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-cond font-bold uppercase tracking-widest text-[#00d9ff] text-sm mb-3">Contact</h4>
          <ul className="space-y-2 text-slate-300 break-words">
            {c.email && <li data-testid="footer-email">{c.email}</li>}
            {c.discord && <li>Discord: {c.discord}</li>}
            {c.instagram && <li>Instagram: {c.instagram}</li>}
            <li>Payments: PayPal · Wise</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 py-5 text-center text-xs text-slate-500">{f.copyright}</div>
    </footer>
  );
}
